// EntitySelectModal.tsx (unchanged, included for completeness)
import { useCallback, useState, useMemo } from 'react';
import { IEntityItem } from '../../../core/interfaces/IEntity';
import { Button, Input } from '../../../ui';
import { useModal } from '../../../core/hooks/useModal';

interface EntitySelectModalProps {
  entities: IEntityItem[];
  currentEntity: IEntityItem | null;
  onSelectEntity: (entity: IEntityItem) => void;
  onRefreshEntities: () => void;
  isEntityLoading: boolean;
}

// Helper function to highlight search terms
const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const EntitySelectModal = () => {
  const { modalData, modalRef } = useModal();
  const { 
    entities, 
    currentEntity, 
    onSelectEntity,
    onRefreshEntities,
    isEntityLoading,
  } = modalData as EntitySelectModalProps;
  
  const [searchTerm, setSearchTerm] = useState('');

  const handleSelect = useCallback((entity: IEntityItem) => {
    onSelectEntity(entity);
    modalRef!.close();
  }, [onSelectEntity, modalRef]);

  const displayEntities = useMemo(() => {
    const baseEntities = entities ? [...entities] : [];
    return baseEntities;
  }, [entities]);

  const filteredDisplayEntities = useMemo(() => {
    if (!searchTerm.trim()) {
      return displayEntities;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return displayEntities.filter(entity => 
      entity.name.toLowerCase().includes(searchLower)
    );
  }, [displayEntities, searchTerm]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="flex flex-row items-center justify-between pb-4 mb-4 border-b border-border">
        <h2 className="text-2xl font-semibold">Select Entity</h2>
        <div>
          <button 
            onClick={() => modalRef!.close()} 
            className="text-text-light hover:text-text transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="">
        <Input
          name="entity-search"
          id="entity-search"
          label="Search Entities"
          type="search"
          placeholder="Search entities..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="md"
          variant="outline"
          radius="sm"
          suffixIcon={true}
          suffixIconClickable={!!searchTerm}
          onSuffixIconClick={clearSearch}
        >
          <div slot="suffix">
            {searchTerm ? (
              <i className="ri-close-line text-lg cursor-pointer"></i>
            ) : (
              <i className="ri-search-line text-lg"></i>
            )}
          </div>
        </Input>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2">
        {/* Search Info */}
        {searchTerm && (
          <div className="px-2 py-1 text-xs text-text-light flex justify-between items-center">
            <span>
              Found {filteredDisplayEntities.length} entity{filteredDisplayEntities.length !== 1 ? 's' : ''}
            </span>
            {filteredDisplayEntities.length !== displayEntities.length && (
              <button 
                onClick={clearSearch}
                className="text-primary hover:text-primary-dark text-xs"
              >
                Show all
              </button>
            )}
          </div>
        )}

        {/* No Results */}
        {searchTerm && filteredDisplayEntities.length === 0 && (
          <div className="text-center py-8 text-text-light">
            <i className="ri-search-line text-3xl mb-2 block"></i>
            <p>No entities found matching "{searchTerm}"</p>
            <button 
              onClick={clearSearch}
              className="text-primary hover:text-primary-dark mt-2 text-sm"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Entities List */}
        {filteredDisplayEntities.map((ent) => {
          const isSelected = (currentEntity && currentEntity.id === ent.id) || (ent.id === null && !currentEntity);

          return (
            <button
              key={ent.id || 'all'}
              onClick={() => handleSelect(ent)}
              className={`
                flex items-center w-full px-4 py-1.5 text-base rounded-sm border 
                transition-all duration-200 text-left
                ${isSelected 
                  ? 'bg-primary-5 border-primary text-primary font-semibold shadow-md' 
                  : 'bg-card border-border text-text hover:bg-background-20'
                }
              `}
            >
              <i className={`ri-building-line text-lg mr-3 ${isSelected ? 'text-primary' : 'text-text-light'}`}></i>
              <span className="flex-1">
                {searchTerm ? highlightText(ent.name, searchTerm) : ent.name}
              </span>
              {isSelected && (
                <i className="ri-check-double-line text-xl ml-auto text-primary"></i>
              )}
            </button>
          );
        })}
        
        {/* Loading Indicator for Refresh */}
        {isEntityLoading && (
           <div className="flex items-center justify-center px-4 py-2.5 text-sm text-info">
             <i className="ri-loader-4-line ri-spin text-lg mr-2"></i> Fetching new entities...
           </div>
        )}
      </div>

       <div className="border-t border-border pt-3">
        <div className="flex justify-between items-center">
          {/* Refresh Button ADDED */}
          <div>
            <Button
              onClick={onRefreshEntities}
              variant="ghost"
              size="sm"
              disabled={isEntityLoading}
            >
               <i className={`ri-refresh-line text-lg mr-2 ${isEntityLoading ? 'ri-spin' : ''}`}></i>
               Refresh List 
            </Button>
          </div>
          <div className="text-sm text-text-light">
            Showing {entities?.length} entity
            {entities?.length !== 1 ? "s" : ""}
          </div>
         
          {/* Close Button was here, moved the whole div for better positioning */}
          <div>
             <Button
               onClick={() => modalRef!.dismiss()}
              variant="ghost"
              size="sm"
              className='ml-auto'
              >
                Close 
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntitySelectModal;